/* eslint-disable max-statements */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import querystring from "querystring";
import UserLogin from "../user-login";
import { DEVICE_TYPE, NUMBER } from "../../../constants/app-constants";
import PageLoader from "../../shared/page-loader";
import { sendCustomGaEvents } from "../../../tracking";
import withOtp from "../../shared/with-otp";
import styles from "./styles.css";
import {
    postInspectionDiyLoginPageView,
    postInspectionDiyLoginGetOtp,
    postInspectionDiyLoginOtpView,
    postInspectionDiyLoginManualOtp,
    postInspectionDiyLoginResendOtp
} from "./tracking";
import Error from "../../shared/error";
import PostInspectionDiyContainer from "../post-inspection-diy-container";
import RtoInfoLoader from "../rto-info-loader";
import SellCar from "./images/sell-car.png";
import { Text } from "@cars24/turbo-web/lib/text";
import { useTokens } from "@cars24/turbo-web/lib/theme-provider";
import { Button } from "@cars24/turbo-web/lib/button";
import AppleLogo from "./images/apple-logo.svg";
import PlayStoreLogo from "./images/play-store.svg";
import Cars24Logo from "./images/logo.svg";
import getDeviceOs from "../../../utils/helpers/get-device-os";
import { APP_ADOPTION_URL } from "../../../constants/seller-constant";

const deviceType = typeof window !== "undefined" && getDeviceOs();

const PostInspectionAuthentication = ({
    history,
    otp,
    showOtpField,
    isLoading,
    setIsLoading = () => {},
    errorMessage,
    onMobileNumberChange,
    onOtpVerify,
    onOtpChange,
    setOtp,
    setIsAutoFilledOTP,
    setShowOtpField,
    logoutUser,
    fetchInspectionDetailsViaLeadTokenConnect,
    fetchInspectionDetailsConnect,
    sendOtpViaLeadTokenConnect,
    verifyOtpViaLeadTokenConnect,
    checkLeadPhoneNumberConnect,
    mobile,
    loaderTitle,
    pageLoading,
    fetchLoginDetailsViaLeadTokenConnect,
    fetchLoginDetailsConnect,
    isLoggedIn,
    secureToken,
    getBifrostProfileDetailsConnect
}) => {
    const { tokens } = useTokens();
    const [isFetchingData, setIsFetchingData] = useState(true);
    const [isUserLoginRequired, setIsUserLoginRequired] = useState(true);
    const [loginDetails, setLoginDetails] = useState({});
    const { maskedPhone: phone, leadToken: token, appointmentId } = loginDetails || {};
    const [isError, setIsError] = useState(false);
    const [showAppDownload, setShowAppDownload] = useState(false);
    const [mobileNumber, setMobileNumber] = useState(mobile);

    const handleProfileDetails = (leadToken = "", mobileNo = "") => {
        checkLeadPhoneNumberConnect({
            token: leadToken,
            phone: mobileNo || mobileNumber
        }).then((response) => {
            if (response.isPhoneChanged) {
                logoutUser();
                setIsUserLoginRequired(true);
                setIsFetchingData(false);
            } else {
                const inspectionDetailPromise = leadToken ? fetchInspectionDetailsViaLeadTokenConnect : fetchInspectionDetailsConnect;
                inspectionDetailPromise(leadToken).then(() => {
                    setIsUserLoginRequired(false);
                    setIsFetchingData(false);
                });
            }
        // eslint-disable-next-line no-unused-vars
        }).catch((e) => {
            logoutUser();
            setIsUserLoginRequired(true);
            setIsFetchingData(false);
        });
    };

    const handleLeadDetails = (leadToken, mobileNo) => {
        handleProfileDetails(leadToken, mobileNo);
    };

    useEffect(() => {
        setIsFetchingData(true);
        const { location: { pathname, search } } = history;
        const queryParams = pathname.split("/inspection-complete/");
        const splittedParams = queryParams && queryParams[1] && queryParams[1].split("?");
        const urlToken = splittedParams[0].split("/")[0];
        const query = search && querystring.parse(search.replace(/\?/g, ""));
        const isLeadToken = query && query.token_type === "lead_token";
        const loginDetailPromise = isLeadToken ? fetchLoginDetailsViaLeadTokenConnect : fetchLoginDetailsConnect;
        loginDetailPromise(urlToken).then((response) => {
            if (query && query.utm_campaign) {
                sendCustomGaEvents({
                    event: "diy_campaign_landing",
                    eventAction: query.utm_campaign,
                    eventCategory: "DIY_UTM",
                    eventLabel: response.appointmentId
                });
            }
            if (response.isMwebBlocked) {
                setShowAppDownload(true);
                setIsFetchingData(false);
            } else {
                setLoginDetails(response);
                if (isLoggedIn && secureToken) {
                    getBifrostProfileDetailsConnect(secureToken)
                        .then((bifrostResponse) => {
                            const phoneNumber = bifrostResponse?.data?.data?.phone_with_country_code?.number;
                            if (phoneNumber) {
                                setMobileNumber(phoneNumber);
                                handleLeadDetails(response.leadToken, phoneNumber);
                            }
                        })
                        .catch(() => {
                            handleLeadDetails(response.leadToken);
                        });
                } else {
                    handleLeadDetails(response.leadToken);
                }

            }
        // eslint-disable-next-line no-unused-vars
        }).catch((e) => {
            setIsError(true);
            setIsFetchingData(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, secureToken]);

    useEffect(() => {
        if (showOtpField && appointmentId && isUserLoginRequired) {
            sendCustomGaEvents({
                ...postInspectionDiyLoginOtpView,
                eventLabel: appointmentId
            });
        }
    }, [appointmentId, showOtpField, isUserLoginRequired]);

    useEffect(() => {
        if (appointmentId && isUserLoginRequired && !isFetchingData) {
            sendCustomGaEvents({
                ...postInspectionDiyLoginPageView,
                eventLabel: appointmentId
            });
        }
    }, [appointmentId, isUserLoginRequired, isFetchingData]);

    const getOtpHandler = () => {
        setIsLoading(true);
        sendOtpViaLeadTokenConnect(token).then(() => {
            setIsLoading(false);
            setShowOtpField(true);
        // eslint-disable-next-line no-unused-vars
        }).catch((e) => {
            setIsLoading(false);
        });
    };

    const getOtp = () => {
        getOtpHandler();
        sendCustomGaEvents({
            ...postInspectionDiyLoginGetOtp,
            eventLabel: appointmentId
        });
    };

    const resendOtp = () => {
        getOtpHandler();
        sendCustomGaEvents({
            ...postInspectionDiyLoginResendOtp,
            eventLabel: appointmentId
        });
    };

    const onBack = () => {
        setShowOtpField(false);
    };

    const otpVerificationHandler = () => {
        if (otp && otp.length === NUMBER.FOUR) {
            onOtpVerify("", verifyOtpViaLeadTokenConnect, { token }).then(() => {
                const inspectionDetailPromise = token ? fetchInspectionDetailsViaLeadTokenConnect : fetchInspectionDetailsConnect;
                inspectionDetailPromise(token).then(() => {
                    setIsUserLoginRequired(false);
                });
            });
        }
    };

    const otpChangeHandler = (number) => {
        onOtpChange(number);
        if (number && number.length === NUMBER.ONE) {
            sendCustomGaEvents({
                ...postInspectionDiyLoginManualOtp,
                eventLabel: appointmentId
            });
        }
    };

    const onCloseHandler = () => {
        history.replace("/sell-used-cars");
    };

    const mobileValidator = () => {
        return !!phone;
    };

    const handleAppDownload = () => {
        window.location.href = APP_ADOPTION_URL.DOWNLOAD_NOW;
    };

    if (isError) return <Error />;
    if (isFetchingData && pageLoading) return <RtoInfoLoader title={loaderTitle ? loaderTitle : "Fetching vehicle details"} />;
    if (isFetchingData) return <PageLoader />;

    if (showAppDownload) {
        return (
            <div styleName={"styles.downloadAppWrapper"}>
                <img src={Cars24Logo} alt="logo" styleName={"styles.logo"}/>
                <div styleName={"styles.carImage"}>
                    <img src={SellCar} alt="sell car" />
                </div>
                <div styleName={"styles.text"}>
                    <Text
                        align="center"
                        as="h4"
                        color={tokens.text_default_text_inverse}
                        fontWeight="bold"
                        size="500"
                        transform="none"
                    >
                    Download the app to know your final price
                    </Text>
                    <Text
                        align="center"
                        as="p"
                        color={"#ffffffa7"}
                        fontWeight="regular"
                        size="250"
                        transform="none"
                    >
                    Live-track your car auction, know your final offer,<br/> and finalise your deal - it’s all done on the app!
                    </Text>
                </div>
                <Button
                    label="Download the app"
                    onClick={() => handleAppDownload()}
                    prefixIcon={<img src={deviceType === DEVICE_TYPE.ANDROID ? PlayStoreLogo : AppleLogo} alt="logo" />}
                    variant="primary"
                    size="large"
                />
            </div>
        );
    }

    return (
        <div styleName={"styles.postInspectionDiyContainer"}>
            {
                isUserLoginRequired ?
                    <UserLogin
                        otp={otp}
                        isEditible={false}
                        onBack={onBack}
                        mobile={phone}
                        onClose={onCloseHandler}
                        onChange={onMobileNumberChange}
                        isLoading={isLoading}
                        errorMessage={errorMessage}
                        onOtpChange={otpChangeHandler}
                        onOtpVerify={otpVerificationHandler}
                        showOtpField={showOtpField}
                        getOtpHandler={getOtp}
                        onResendClick={resendOtp}
                        setOtp={setOtp}
                        setIsAutoFilledOTP={setIsAutoFilledOTP}
                        mobileValidator={mobileValidator}
                        otpLength={NUMBER.FOUR}
                    />
                    :
                    <PostInspectionDiyContainer />

            }
        </div>
    );
};

PostInspectionAuthentication.propTypes = {
    history: PropTypes.object,
    inspectionResponse: PropTypes.any,
    onLoginSuccessCallback: PropTypes.func,
    otp: PropTypes.string,
    showOtpField: PropTypes.bool,
    isLoading: PropTypes.bool,
    errorMessage: PropTypes.string,
    onMobileNumberChange: PropTypes.func,
    onOtpVerify: PropTypes.func,
    onOtpChange: PropTypes.func,
    setOtp: PropTypes.func,
    setIsAutoFilledOTP: PropTypes.func,
    setShowOtpField: PropTypes.func,
    logoutUser: PropTypes.func,
    fetchInspectionDetailsViaLeadTokenConnect: PropTypes.func,
    fetchInspectionDetailsConnect: PropTypes.func,
    fetchCarDetailsConnet: PropTypes.func,
    fetchAppointmentDetailsConnect: PropTypes.func,
    setIsLoading: PropTypes.func,
    sendOtpViaLeadTokenConnect: PropTypes.func,
    verifyOtpViaLeadTokenConnect: PropTypes.func,
    checkLeadPhoneNumberConnect: PropTypes.func,
    mobile: PropTypes.any,
    loaderTitle: PropTypes.string,
    pageLoading: PropTypes.bool,
    fetchLoginDetailsViaLeadTokenConnect: PropTypes.func,
    fetchLoginDetailsConnect: PropTypes.func,
    isLoggedIn: PropTypes.bool,
    secureToken: PropTypes.string,
    getBifrostProfileDetailsConnect: PropTypes.func
};

export default withOtp(PostInspectionAuthentication);
